import { useRef } from 'react'
import {
  LocalVideo,
  MeetingStatus,
  useLocalVideo,
  useMeetingStatus,
  useToggleLocalMute
} from 'amazon-chime-sdk-component-library-react'
import { useDragAndDrop } from '@hooks/useDragAndDrop'
import { useLocalParticipantInitials } from '@hooks/useLocalParticipantInitials'
import { ParticipantBadge } from '@components/atoms/ParticipantBadge/ParticipantBadge'
import { SpeakingIndicator } from '@components/atoms/SpeakingIndicator/SpeakingIndicator'
import { useLocalUserSpeaking } from '@hooks/useLocalUserSpeaking'
import { MEETING_LAYOUT, VIDEO_CLASSES } from '@constants/meetingLayout'

type DraggableLocalVideoProps = {
  containerRef: React.RefObject<HTMLDivElement>
  platform: 'mobile' | 'desktop'
}

export const DraggableLocalVideo = ({ containerRef, platform }: DraggableLocalVideoProps) => {
  const localVideoRef = useRef<HTMLDivElement>(null)
  const { isVideoEnabled } = useLocalVideo()
  const isLocalSpeaking = useLocalUserSpeaking()
  const status = useMeetingStatus()
  const { initials, firstName } = useLocalParticipantInitials()
  const { muted } = useToggleLocalMute()

  const { position, isDragging, isHovered, setIsHovered, handleMouseDown, handleTouchStart } = useDragAndDrop({
    containerRef,
    elementRef: localVideoRef
  })

  const cursorClass = isDragging ? 'cursor-grabbing' : 'cursor-grab'
  const scaleClass = isHovered && !isDragging ? 'scale-110' : 'scale-100'

  return (
    <div
      ref={localVideoRef}
      className={`absolute z-10 overflow-hidden rounded-xl bg-surface-dark shadow-2xl transition-transform duration-250 ${cursorClass} ${scaleClass}`}
      style={{
        width: `${MEETING_LAYOUT[platform].pip.width}px`,
        height: `${MEETING_LAYOUT[platform].pip.height}px`,
        left: `${position.x}px`,
        top: `${position.y}px`,
        touchAction: 'none'
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Video or avatar */}
      <div className="absolute inset-0 flex items-center justify-center">
        {isVideoEnabled && status === MeetingStatus.Succeeded ? (
          <LocalVideo className={`${VIDEO_CLASSES.fullCover} ${VIDEO_CLASSES.videoElement}`} />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-surface-dark">
            <SpeakingIndicator isSpeaking={isLocalSpeaking && !isVideoEnabled} size="sm">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-medium">
                <span className="text-xl font-bold text-primary">{initials}</span>
              </div>
            </SpeakingIndicator>
          </div>
        )}
      </div>

      {/* Badge */}
      <div className="pointer-events-none">
        <ParticipantBadge name={firstName} position="bottom-left" isMuted={muted} />
      </div>
    </div>
  )
}
